import React, { type FC, type KeyboardEvent, type MouseEvent, type PropsWithChildren } from 'react'
import { type SxProps, TableCell, TableRow, type Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

import { createHandlerWithoutPropagation } from '../../utils'
import { Hyphen } from '../Typography'
import { TableCheckbox } from './Checkbox'
import {
    useColumn,
    useColumnNames,
    useHasColumnView,
    useHasTrailingColumn,
    useIsRowActive,
    useOnRowClick,
    useOnRowOpenInNewTab,
    useRowRef,
    useSelection,
} from './State'
import { TrailingCell } from './TrailingColumn'
import type { Columns, ColumnType, Line } from './Types'

const wrappingCellStyle: SxProps<Theme> = { wordBreak: 'break-all' }
/** Custom content that can't ellipsize itself (e.g. a row of links) is clipped at the cell's edge */
const oneLineCellStyle: SxProps<Theme> = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const clickableRowStyle: SxProps<Theme> = { cursor: 'pointer' }
const columnTypeStyles: Readonly<Record<ColumnType, SystemStyleObject<Theme>>> = {
    number: { fontVariantNumeric: 'tabular-nums' },
    datetime: { fontVariantNumeric: 'tabular-nums', color: 'grey.600' },
}

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === ''
const isPlainText = (value: unknown): value is string | number => (typeof value === 'string' && value !== '') || typeof value === 'number'

const Cell = <L extends Line>({ name, row, index }: PropsWithChildren<{ name: keyof Columns<L>, row: L, index: number }>): ReturnType<FC> => {
    const { customCellRender = undefined, sx = null, type = null } = useColumn<L, keyof Columns<L>>(name)
    const hasColumnView = useHasColumnView()
    const value = row[name]
    /** One line can hide the end of the text, so a plain text cell carries all of it */
    const title = hasColumnView && !customCellRender && isPlainText(value) ? String(value) : undefined

    return (
        <TableCell
            align={type === 'number' ? 'right' : undefined}
            title={title}
            sx={{ ...(hasColumnView ? oneLineCellStyle : wrappingCellStyle), ...(type && columnTypeStyles[type]), ...sx }}
        >
            {customCellRender ? customCellRender(value, row, index) : isEmptyValue(value) ? <Hyphen /> : <>{value}</>}
        </TableCell>
    )
}

const MIDDLE_BUTTON = 1

const isNewTabClick = (e: MouseEvent): boolean => e.metaKey || e.ctrlKey || e.shiftKey

const INNER_CONTROLS = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    '[role="button"]',
    '[role="menuitem"]',
    '[role="checkbox"]',
    '[contenteditable="true"]',
    '[contenteditable=""]',
].join(', ')

/**
 * A middle-click belongs to the row only on the row's own DOM, outside of its links and controls
 * Events from the row's portals (e.g. an open actions menu) reach it through the React tree too, so they're excluded
 */
const isOwnMiddleClick = (e: MouseEvent<HTMLTableRowElement>): boolean =>
    e.button === MIDDLE_BUTTON &&
    e.target instanceof Element &&
    e.currentTarget.contains(e.target) &&
    (e.target === e.currentTarget || e.target.closest(INNER_CONTROLS) === null)

/**
 * Only the row's own Enter acts: a key pressed on a control inside the row belongs to that control, and a held key doesn't act again
 * Plain Enter opens the row, Cmd/Ctrl+Enter opens it in a new tab, any other modified Enter is left to the page
 */
const onOwnEnter = (e: KeyboardEvent<HTMLTableRowElement>, open: VoidFunction | null, openInNewTab: VoidFunction | null): void => {
    if (e.key !== 'Enter' || e.target !== e.currentTarget || e.repeat || e.shiftKey || e.altKey) {
        return
    }

    if (e.ctrlKey || e.metaKey) {
        openInNewTab?.()
        return
    }

    open?.()
}

const onClick = (e: MouseEvent, open: VoidFunction | null, openInNewTab: VoidFunction | null): void => {
    if (openInNewTab && isNewTabClick(e)) {
        openInNewTab()
        return
    }

    open?.()
}

const onMiddleButton = (e: MouseEvent<HTMLTableRowElement>, handle: VoidFunction): void => {
    if (isOwnMiddleClick(e)) {
        handle()
    }
}

export const Row = <L extends Line>({ index, row }: PropsWithChildren<{ index: number, row: L }>): ReturnType<FC> => {
    const names = useColumnNames()
    const onRowClick = useOnRowClick<L>()
    const onRowOpenInNewTab = useOnRowOpenInNewTab<L>()
    const active = useIsRowActive(row)
    const rowRef = useRowRef<L>()
    const hasTrailingColumn = useHasTrailingColumn()

    const [selected, toggleSelection] = useSelection(row)

    const open = onRowClick && (() => onRowClick(row))
    const openInNewTab = open && onRowOpenInNewTab && (() => onRowOpenInNewTab(row))
    const interactive = Boolean(open)

    return (
        <TableRow
            key={`table-row-${index}`}
            ref={rowRef ? (element: HTMLTableRowElement | null) => rowRef(row, element) : undefined}
            onClick={interactive ? (e) => onClick(e, open, openInNewTab) : undefined}
            onAuxClick={openInNewTab ? (e) => onMiddleButton(e, openInNewTab) : undefined}
            /** Keeps the middle button from starting the browser's autoscroll */
            onMouseDown={openInNewTab ? (e) => onMiddleButton(e, () => e.preventDefault()) : undefined}
            tabIndex={interactive ? 0 : undefined}
            onKeyDown={interactive ? (e) => onOwnEnter(e, open, openInNewTab) : undefined}
            hover={interactive}
            selected={active}
            aria-current={active ? 'true' : undefined}
            sx={interactive ? clickableRowStyle : undefined}
        >
            {selected !== null && (
                <TableCell key={`checkbox-${index}`} padding="checkbox">
                    <TableCheckbox data-testid={`checkbox-${index}`} checked={selected} onClick={createHandlerWithoutPropagation(toggleSelection)} />
                </TableCell>
            )}

            {names.map((name) => <Cell key={String(name)} name={name} row={row} index={index} />)}

            {hasTrailingColumn && <TrailingCell index={index} row={row} />}
        </TableRow>
    )
}
